import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const TermsAndConditions = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800 rounded-xl p-8 shadow-xl">
            
            {/* Header */}
            <div className="mb-8 pb-6 border-b border-gray-700">
              <div className="flex items-center space-x-4 mb-4">
                <Link 
                  to="/" 
                  className="text-cyan-400 hover:text-cyan-300 transition-colors flex items-center space-x-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
                  </svg>
                  <span>Retour à l'accueil</span>
                </Link>
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Termes et Conditions de Pimp My Drop</h1>
              <p className="text-gray-400">Date d'entrée en vigueur: 8 juin 2025</p>
            </div>

            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">1. Introduction</h2>
              <p className="text-gray-300 leading-relaxed">
                Bienvenue sur "pimp my drop" (ci-après "nous", "notre" ou "nos"). La protection de vos données personnelles 
                est une priorité pour nous. Cette politique de confidentialité vise à vous informer de manière claire et 
                transparente sur la manière dont nous collectons, utilisons, protégeons et partageons vos informations 
                lorsque vous utilisez notre site web et nos services.
              </p>
            </section>

            {/* Données Collectées */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">2. Données Collectées</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Dans le cadre de l'utilisation de notre site, nous collectons uniquement les données strictement 
                nécessaires à la fourniture de nos services.
              </p>
              <ul className="space-y-4 text-gray-300">
                <li>
                  <strong className="text-white">Données d'identification via des services tiers :</strong> Pour vous inscrire et vous connecter à "pimp my drop", 
                  vous pouvez utiliser vos comptes Google, Twitch ou Discord. En choisissant l'une de ces options, vous nous autorisez 
                  à collecter votre adresse e-mail et/ou un identifiant unique (ID utilisateur) fourni par ces plateformes. Nous ne 
                  collectons aucune autre information personnelle (comme votre nom, votre liste d'amis ou vos publications) de ces services.
                </li>
                <li>
                  <strong className="text-white">Données téléversées par les utilisateurs :</strong> Nous collectons et stockons les designs que vous 
                  choisissez de téléverser sur notre plateforme.
                </li>
                <li>
                  <strong className="text-white">Données de navigation :</strong> Nous pouvons collecter des informations techniques minimales, 
                  telles que votre adresse IP, à des fins de sécurité et de maintenance.
                </li>
              </ul>
            </section>

            {/* Utilisation des Données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">3. Utilisation des Données</h2>
              <p className="text-gray-300 leading-relaxed mb-4">Vos données sont utilisées pour les finalités suivantes :</p>
              <ul className="space-y-3 text-gray-300">
                <li>
                  <strong className="text-white">Fourniture de nos services :</strong> Votre adresse e-mail ou votre identifiant unique est utilisé 
                  pour créer et gérer votre compte utilisateur, et notamment pour vous permettre de participer à notre système de vote 
                  pour les designs proposés.
                </li>
                <li>
                  <strong className="text-white">Gestion des designs :</strong> Les designs que vous téléversez sont utilisés dans le but d'être 
                  potentiellement sélectionnés pour la création de produits sur notre boutique Shopify.
                </li>
                <li>
                  <strong className="text-white">Sécurité et maintenance :</strong> Nous utilisons les données de navigation pour assurer la sécurité 
                  de notre site et pour diagnostiquer d'éventuels problèmes techniques.
                </li>
                <li>
                  <strong className="text-white">Communications :</strong> Nous pouvons utiliser votre adresse e-mail pour vous envoyer des informations 
                  importantes concernant votre compte ou nos services (par exemple, la sélection de votre design).
                </li>
              </ul>
            </section>

            {/* Cession des Droits */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">4. <strong>{t('termsSection4Title')}</strong></h2>
              <div className="bg-yellow-900 border border-yellow-600 rounded-lg p-4 mb-4">
                <p className="text-yellow-200 leading-relaxed">
                  <strong>{t('termsImportant')}:</strong> {t('termsSection4ImportantText')} {t('termsSection4AllowsUs')}
                </p>
              </div>

              <ul className="space-y-2 text-gray-300 mb-6">
                <li>• {t('termsSection4Bullet1')}</li>
                <li>• {t('termsSection4Bullet2')}</li>
              </ul>
              <div className="bg-gray-700 p-4 rounded-lg">
                <p className="text-gray-300 leading-relaxed">
                  <strong className="text-white">{t('termsSection4MinorsTitle')}:</strong> {t('termsSection4MinorsText')}
                </p>
              </div>
            </section>

            {/* Obligations de l'Utilisateur */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">5. Obligations et Responsabilité de l'Utilisateur concernant les Designs Téléversés</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                En téléversant un design sur le site "pimp my drop", vous déclarez et garantissez que :
              </p>
              <div className="space-y-4">
                <div className="bg-gray-700 p-4 rounded-lg">
                  <p className="text-gray-300 mb-2">
                    <strong className="text-white">1. Vous êtes le propriétaire original de l'œuvre</strong> ou que vous disposez de toutes 
                    les autorisations et licences nécessaires pour l'utiliser et en céder les droits conformément à l'article 4 
                    des présentes conditions.
                  </p>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <p className="text-gray-300 mb-3">
                    <strong className="text-white">2. Le design est une création originale</strong> et ne viole aucun droit de propriété 
                    intellectuelle d'un tiers. Cela inclut, sans s'y limiter :
                  </p>
                  <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
                    <li><strong>Aucune image sous copyright :</strong> Le design ne doit contenir aucune image, photographie, ou illustration dont vous ne détenez pas les droits d'auteur.</li>
                    <li><strong>Aucune fonte (police de caractères) sous copyright :</strong> Les fontes utilisées dans le design doivent être libres de droits pour un usage commercial ou vous devez détenir une licence appropriée vous permettant de les utiliser et de les céder.</li>
                    <li><strong>Aucun élément de marque déposée :</strong> Le design ne doit comporter aucun logo, nom de marque, slogan ou autre élément protégé par le droit des marques appartenant à un tiers.</li>
                  </ul>
                </div>
                <div className="bg-gray-700 p-4 rounded-lg">
                  <p className="text-gray-300">
                    <strong className="text-white">3. Le contenu n'est pas illégal, diffamatoire, obscène, haineux</strong> ou ne contrevient 
                    à aucune loi en vigueur.
                  </p>
                </div>
              </div>
              <div className="bg-red-900 border border-red-600 rounded-lg p-4 mt-6">
                <p className="text-red-200 leading-relaxed">
                  <strong>Responsabilité :</strong> En tant qu'utilisateur, vous êtes seul et unique responsable du contenu que vous téléversez. 
                  En cas de réclamation, de litige ou de poursuite judiciaire par un tiers affirmant que votre design viole ses droits 
                  de propriété intellectuelle ou tout autre droit, vous vous engagez à indemnifier et à défendre pleinement "pimp my drop" 
                  et la société AKS - FZCO contre toutes les conséquences, dommages, pertes et frais (y compris les frais d'avocat) qui 
                  pourraient en résulter.
                </p>
              </div>
              <p className="text-gray-300 leading-relaxed mt-4">
                "pimp my drop" se réserve le droit de retirer sans préavis tout design qui semblerait manifestement enfreindre ces règles.
              </p>
            </section>

            {/* Partage des Données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">6. Partage des Données</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Nous ne vendons ni ne louons vos données personnelles. Vos informations ne sont partagées qu'avec les tiers suivants, 
                dans le respect de la présente politique :
              </p>
              <ul className="space-y-3 text-gray-300">
                <li>
                  <strong className="text-white">Shopify :</strong> Les designs sélectionnés seront transférés à notre partenaire Shopify 
                  pour la création et la gestion des produits dérivés.
                </li>
                <li>
                  <strong className="text-white">Prestataires de services :</strong> Nous pouvons faire appel à des prestataires pour 
                  l'hébergement de notre site et la maintenance de nos systèmes. Ces prestataires n'ont accès à vos informations que 
                  dans le cadre de l'exécution de leurs missions et sont tenus à une obligation de confidentialité.
                </li>
                <li>
                  <strong className="text-white">Autorités légales :</strong> Nous pouvons être amenés à divulguer vos informations si la loi 
                  l'exige ou si nous estimons de bonne foi que cette divulgation est nécessaire pour protéger nos droits, votre sécurité 
                  ou celle d'autrui.
                </li>
              </ul>
            </section>

            {/* Conservation des Données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">7. Conservation des Données</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Nous ne conservons vos données que pour la durée nécessaire aux finalités pour lesquelles elles ont été collectées.
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>• Votre adresse e-mail et/ou identifiant unique sont conservés tant que votre compte utilisateur est actif sur "pimp my drop".</li>
                <li>• Les designs téléversés sont conservés pour une durée indéterminée, conformément à la cession de droits que vous avez acceptée.</li>
              </ul>
            </section>

            {/* Sécurité des Données */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">8. Sécurité des Données</h2>
              <p className="text-gray-300 leading-relaxed">
                Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données 
                personnelles contre tout accès non autorisé, toute modification, divulgation ou destruction.
              </p>
            </section>

            {/* Cookies */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">9. Cookies et Technologies Similaires</h2>
              <p className="text-gray-300 leading-relaxed">
                Nous utilisons des cookies uniquement pour les fonctionnalités essentielles à votre connexion et à la gestion de votre 
                session utilisateur. Ces cookies ne sont pas utilisés à des fins de suivi publicitaire ou d'analyse comportementale. 
                Vous pouvez gérer ou refuser l'utilisation des cookies via les paramètres de votre navigateur internet.
              </p>
            </section>

            {/* Vos Droits */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">10. Vos Droits</h2>
              <p className="text-gray-300 leading-relaxed mb-4">
                Conformément au Règlement Général sur la Protection des Données (RGPD), vous disposez des droits suivants concernant 
                vos données personnelles :
              </p>
              <ul className="space-y-2 text-gray-300">
                <li>• <strong className="text-white">Droit d'accès :</strong> Vous avez le droit de demander une copie des informations personnelles que nous détenons à votre sujet.</li>
                <li>• <strong className="text-white">Droit de rectification :</strong> Vous pouvez demander la correction des informations inexactes ou incomplètes.</li>
                <li>• <strong className="text-white">Droit à l'effacement ("droit à l'oubli") :</strong> Vous pouvez demander la suppression de votre compte et des données personnelles associées. Notez que ce droit ne s'applique pas aux designs que vous avez téléversés, en raison de la cession de droits.</li>
                <li>• <strong className="text-white">Droit à la limitation du traitement.</strong></li>
                <li>• <strong className="text-white">Droit à la portabilité des données.</strong></li>
              </ul>
              <p className="text-gray-300 leading-relaxed mt-4">
                Pour exercer ces droits, vous pouvez nous contacter à l'adresse suivante : <strong className="text-cyan-400">contact@pimpmydrop.com</strong>.
              </p>
            </section>

            {/* Modifications */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">11. Modifications de la Politique de Confidentialité</h2>
              <p className="text-gray-300 leading-relaxed">
                Nous nous réservons le droit de modifier cette politique de confidentialité à tout moment. Toute modification sera 
                publiée sur cette page avec la date de la dernière mise à jour. Nous vous encourageons à consulter régulièrement 
                cette page pour rester informé de la manière dont nous protégeons vos données.
              </p>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-cyan-400 mb-4">12. Contact</h2>
              <p className="text-gray-300 leading-relaxed">
                Pour toute question ou préoccupation concernant cette politique de confidentialité ou vos données personnelles, 
                veuillez nous contacter à :
              </p>
              <p className="text-cyan-400 font-semibold mt-2">contact@pimpmydrop.com</p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions; 